// Import React and authentication hook
import React, { useState, useEffect } from 'react';
// Hook that provides login functionality from AuthContext
import { useAuth } from '../contexts/AuthContext';
// Icons for show/hide password toggle and error display
import { EyeIcon, EyeSlashIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { trackButtonClick, addUserActionBreadcrumb } from '../utils/sentryHelpers';

/**
 * Login Component
 * This is the main login screen that users see when they first open the app
 * It handles user authentication and provides demo credentials for testing
 */
const Login: React.FC = () => {
  // State variables for the login form
  const [companyCode, setCompanyCode] = useState('');        // Company code for SWS users
  const [username, setUsername] = useState('');              // What the user types in username field
  const [password, setPassword] = useState('');              // What the user types in password field
  const [showPassword, setShowPassword] = useState(false);   // Whether password should be visible (not hidden as dots)
  const [showCompanyInfo, setShowCompanyInfo] = useState(false); // Whether to show company code help info
  
  // Get authentication functions from the AuthContext
  // This connects to the app's authentication system
  const { login, isLoading, error, clearError } = useAuth();

  /**
   * handleSubmit Function
   * Called when user clicks "Sign in" button or presses Enter in form
   * Validates input and attempts to log the user in
   */
  const handleSubmit = trackButtonClick('Login Submit', async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent page refresh (default form behavior)
    
    // Don't proceed if username or password is empty (after removing spaces)
    if (!username.trim() || !password.trim()) {
      return;
    }

    return Sentry.startSpan(
      {
        op: "ui.action",
        name: "Login Form Submit",
      },
      async () => {
        try {
          addUserActionBreadcrumb('Login attempt', { 
            hasCompanyCode: !!companyCode,
            username: username.trim() 
          });
          
          // Attempt to log in using the authentication service
          // Pass company code (if provided), username, and password
          await login(companyCode.trim(), username.trim(), password);
        } catch (err) {
          // If login fails, capture in Sentry
          Sentry.captureException(err, {
            tags: { component: 'Login' },
            extra: { username: username.trim(), hasCompanyCode: !!companyCode }
          });
          console.error('Login error:', err);
        }
      }
    );
  });

  /**
   * handleInputChange Function
   * Called whenever user types in username or password fields
   * Clears any error messages when user starts typing again
   */
  const handleInputChange = () => {
    if (error) {
      clearError(); // Remove error message since user is trying again
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-secondary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex items-center justify-center">
            <img src={`${process.env.PUBLIC_URL}/Rebound_Logo.png`} alt="Rebound Dynamics Logo" className="h-24 w-auto" />
          </div>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-secondary-700 mb-1">
                Username / Email
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  handleInputChange();
                }}
                className="appearance-none relative block w-full px-3 py-2 border border-secondary-300 placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Enter your username or email"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-secondary-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    handleInputChange();
                  }}
                  className="appearance-none relative block w-full px-3 py-2 pr-10 border border-secondary-300 placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={trackButtonClick('Toggle Password Visibility', () => setShowPassword(!showPassword))}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-secondary-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-secondary-400" />
                  )}
                </button>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="company-code" className="block text-sm font-medium text-secondary-700">
                  Company Code
                </label>
                <button
                  type="button"
                  onClick={trackButtonClick('Toggle Company Info', () => setShowCompanyInfo(!showCompanyInfo))}
                  className="text-primary-600 hover:text-primary-500"
                >
                  <InformationCircleIcon className="h-5 w-5" />
                </button>
              </div>
              <input
                id="company-code"
                name="companyCode"
                type="text"
                autoComplete="organization"
                value={companyCode}
                onChange={(e) => {
                  setCompanyCode(e.target.value);
                  handleInputChange();
                }}
                className="appearance-none relative block w-full px-3 py-2 border border-secondary-300 placeholder-secondary-500 text-secondary-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="If applicable (SWS users)"
              />
              {showCompanyInfo && (
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
                  <p className="font-medium mb-1">When to use Company Code:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li><strong>SWS users:</strong> Enter your company code</li>
                    <li><strong>Smart users:</strong> Leave this field blank</li>
                  </ul>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="flex items-center p-4 text-sm text-error-800 bg-error-50 border border-error-300 rounded-md animate-fade-in">
              <ExclamationTriangleIcon className="h-5 w-5 mr-2 text-error-600" />
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading || !username.trim() || !password.trim()}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;